// 章要約グリッドの本文。長い章だけ「続きを読む」で折りたたみ、行の高さを揃える。
// 書籍由来の本文は一切削らず、DOM には常に全文が存在する（details 内も含む）。
// アコーディオンの見た目は各ページ FAQ の <details> パターンに合わせる。

const LEAD_MIN = 90; // これ以上になった最初の文末で区切る
const FOLD_THRESHOLD = 160; // これ以下はそのまま1段落で出す

/**
 * desc をリード文と残りに分割する。分割点は文末に限る。
 * 日本語は「。」、英語は「. 」（ピリオド+空白）を文末とみなす。
 */
export function splitDesc(text: string, lang: 'ja' | 'en' = 'ja'): { lead: string; rest: string } {
  const t = String(text ?? '');
  // 英語は同じ情報量でも文字数が伸びるため閾値を上げる
  const foldThreshold = lang === 'en' ? FOLD_THRESHOLD * 2 : FOLD_THRESHOLD;
  const leadMin = lang === 'en' ? LEAD_MIN * 2 : LEAD_MIN;
  if (t.length <= foldThreshold) return { lead: t, rest: '' };

  const sentenceEnd = lang === 'en' ? /\.\s/g : /。/g;
  let cut = -1;
  let m: RegExpExecArray | null;
  sentenceEnd.lastIndex = 0;
  while ((m = sentenceEnd.exec(t)) !== null) {
    const end = m.index + (lang === 'en' ? 1 : m[0].length);
    if (end >= leadMin) {
      cut = end;
      break;
    }
  }
  // 文末が見つからない/切っても残りが無い場合は折りたたまない
  if (cut === -1 || cut >= t.length) return { lead: t, rest: '' };

  return { lead: t.slice(0, cut), rest: t.slice(cut).trim() };
}

export default function CurriculumDesc({
  text,
  lang = 'ja',
}: {
  text: string;
  lang?: 'ja' | 'en';
}) {
  const { lead, rest } = splitDesc(text, lang);

  // 既存の各ページの本文 <p> と同じ字体・行送りを維持する
  const bodyCls =
    lang === 'en'
      ? "font-['Playfair_Display',serif] text-sm text-[#4a4638] leading-[1.85]"
      : "font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]";
  const openLabel = lang === 'en' ? 'Read more' : '続きを読む';
  const closeLabel = lang === 'en' ? 'Close' : '閉じる';

  // 空行（\n\n）で区切られた本文を複数段落へ描画する。文言は一切変えず分配のみ。
  const paras = (s: string) =>
    s
      .split(/\n{2,}/)
      .map((x) => x.trim())
      .filter(Boolean);

  const block = (s: string, extraCls = '') =>
    paras(s).map((p, i) => (
      <p key={i} className={`${i === 0 ? extraCls : 'mt-4'} ${bodyCls}`.trim()}>
        {p}
      </p>
    ));

  if (!rest) {
    return <>{block(text)}</>;
  }

  return (
    <>
      {block(lead)}
      <details className="group mt-2">
        <summary
          className={`cursor-pointer list-none text-[13px] tracking-[0.08em] text-[#8f7a56] hover:text-[#1a2942] transition-colors flex items-center gap-1.5 ${
            lang === 'en' ? "font-['Playfair_Display',serif]" : "font-['Noto_Serif_JP',serif]"
          }`}
        >
          <span className="group-open:hidden">{openLabel}</span>
          <span className="hidden group-open:inline">{closeLabel}</span>
          <i className="ri-arrow-down-s-line text-base transition-transform group-open:rotate-180"></i>
        </summary>
        {block(rest, 'mt-2')}
      </details>
    </>
  );
}
